import React, { useCallback } from "react";
import {
  FirstContainer,
  AgentIcon,
  ModelRow,
  SecondContainer,
  ThirdContainer,
  StarterQuestion,
} from "../../shared.styled";
import {
  ContainerWithRelative,
  ContentWithOpacity,
  StarterQuestionText,
} from "./AgentView.styled";
import { Typography, Icon, ChatInput, Tooltip } from "@ann/web-components";
import { useHomeChat } from "../../../../context";
import {
  CHAT_INPUT_ADD_MENU_ITEMS,
  DOCUMENT_SEARCH_PROPS,
} from "../../constants";
import ChatSkeleton from "../ChatSkeleton";
import AgentViewSkeleton from "./AgentViewSkeleton";

const getAgentInitials = (name) => {
  if (!name || typeof name !== "string") return "CB";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const DEFAULT_AGENT_NAME = "Strategy Assistant";
const DEFAULT_MODEL = "Claude Sonnet 5";
const DEFAULT_DESCRIPTION =
  "I can help you streamline your work focused on your goals. If you'd like, ask me something to get started.";

const AgentView = ({
  selectedAgentId,
  attachments,
  onFilesSelected,
  onAttachmentRemove,
  onClearAttachments,
  onAddMenuChange,
  onModeMenuChange,
  onModeTagClose,
  clearAttachments,
  isLoadingMessages = false,
  showModelSelector = false,
  modelAlignMenu,
  documents = [],
  onDocumentSelect,
  onDocumentDelete,
  onDocumentSearchMenuChange,
}) => {
  const {
    selectedAgent,
    sendMessage,
    agentIsTyping,
    conversationStarters,
    selectedConversationId,
    providers,
    selectedProvider,
    selectedModel,
    onModelChange,
    onProviderChange,
    isLoadingAgentDetails,
  } = useHomeChat();

  const agentName =
    selectedAgent?.name || selectedAgentId || DEFAULT_AGENT_NAME;
  const modelText =
    selectedAgent?.metadata?.model ||
    selectedAgent?.model ||
    DEFAULT_MODEL;
  const description =
    selectedAgent?.description ||
    selectedAgent?.metadata?.description ||
    DEFAULT_DESCRIPTION;



  const handleSendMessage = useCallback(
    (messageText) => {
      const list = attachments || [];
      const fileUuids = list
        .map((a) => a.fileUuid || a.document_uuid)
        .filter((uuid) => uuid);
      const attachmentDetails =
        list.length > 0
          ? list.map((a) => ({
              fileUuid: a.fileUuid || a.document_uuid,
              name: a.name ?? "Document",
              fileType: a.fileType ?? (a.name?.includes?.(".") ? a.name.split(".").pop() : ""),
              isLoading: a.isLoading === true,
            }))
          : undefined;
      const llmConfig =
        showModelSelector && selectedProvider && selectedModel
          ? { llm_model: selectedModel, llm_model_provider: selectedProvider }
          : undefined;

      sendMessage(messageText, {
        conversationId: selectedConversationId || "",
        agentId: selectedAgentId,
        fileUuids: fileUuids.length > 0 ? fileUuids : undefined,
        attachmentDetails,
        ...(llmConfig && { llmConfig }),
      });
      onClearAttachments?.();
    },
    [sendMessage, selectedConversationId, selectedAgentId, attachments, onClearAttachments, showModelSelector, selectedProvider, selectedModel],
  );

  return (
    <ContainerWithRelative>
      {/* Loading overlay for first conversation load */}
      <ChatSkeleton show={isLoadingMessages} />
      <ContentWithOpacity $isLoading={isLoadingMessages}>
        {isLoadingAgentDetails ? (
          <AgentViewSkeleton />
        ) : (
          <>
            <FirstContainer>
          <AgentIcon>{getAgentInitials(agentName)}</AgentIcon>
          <Typography category="title" size="medium" weight="bold">
            {agentName}
          </Typography>
          <ModelRow>
            <Icon name="check" size="small" color="success2" />
            <Typography
              category="caption"
              size="small"
              weight="regular"
              color="body"
            >
              Model used by the creator: {modelText}
            </Typography>
          </ModelRow>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
            maxCharacters={100}

          >
            {description}
          </Typography>
        </FirstContainer>
        <SecondContainer>
          <ChatInput
            className={"chat-input"}
            placeholder="Ask anything"
            allowUploadFiles
            attachments={attachments}
            onFilesSelected={onFilesSelected}
            onAttachmentRemove={onAttachmentRemove}
            onAddMenuChange={onAddMenuChange}
            onModeMenuChange={onModeMenuChange}
            onModeTagClose={onModeTagClose}
            addMenuItems={CHAT_INPUT_ADD_MENU_ITEMS}
            documentSearchProps={DOCUMENT_SEARCH_PROPS}
            documents={documents}
            onDocumentSelect={onDocumentSelect}
            onDocumentDelete={onDocumentDelete}
            onDocumentSearchDeleteClick={onDocumentDelete}
            onDocumentSearchMenuChange={onDocumentSearchMenuChange}
            isProcessing={agentIsTyping}
            onSend={handleSendMessage}
            {...(showModelSelector && providers?.length > 0 && {
              providers,
              selectedProvider,
              selectedModel,
              onModelChange,
              onProviderChange,
              ...(modelAlignMenu && { modelAlignMenu }),
            })}
          />
        </SecondContainer>
        <ThirdContainer>
          {conversationStarters.map((question, index) => (
            <Tooltip
              key={index}
              content={question}
              position="top"
              align="end"
              arrow={false}
              zIndex={99999999}
            >
              <StarterQuestion onClick={() => handleSendMessage(question)}>
                <StarterQuestionText>
                  <Typography
                    category="body"
                    size="small"
                    weight="regular"
                    color="body"
                  >
                    {question}
                  </Typography>
                </StarterQuestionText>
              </StarterQuestion>
            </Tooltip>
          ))}
        </ThirdContainer>
          </>
        )}
      </ContentWithOpacity>
    </ContainerWithRelative>
  );
};

export default AgentView;
